import { USERNAME_ISSUE_MESSAGE, USERNAME_MAX_LENGTH, usernameIssue } from "@loot-os/api-contract";
import { Alert } from "@loot-os/design-system/core/Alert";
import { Button } from "@loot-os/design-system/core/Button";
import { Input } from "@loot-os/design-system/core/Input";
import { Modal, ModalClose, ModalFooter } from "@loot-os/design-system/core/Modal";
import { useId, useState } from "react";

import { useTerminology } from "../lib/terminology.js";
import { usernameErrorMessage, useSaveUsername } from "../lib/username.js";

/**
 * The shopper's username form: one field, the rules checked as they type (the same
 * `usernameIssue` the server applies), and the server's word on whether the name is taken.
 * Opened from the Account page, chat's composer and the Forge.
 */
export function UsernameDialog({
  open,
  onOpenChange,
  username,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current username, null while none is chosen. */
  username: string | null;
}) {
  const formId = useId();
  const { battle } = useTerminology();
  const save = useSaveUsername();
  const [draft, setDraft] = useState(username ?? "");
  const [error, setError] = useState<string | null>(null);
  const candidate = draft.trim();
  // Nothing is said about an empty field; the rules speak once the shopper has typed.
  const issue = candidate ? usernameIssue(candidate) : null;
  const unchanged = candidate === username;

  const reset = (next: boolean) => {
    if (next) {
      setDraft(username ?? "");
      setError(null);
    }
    onOpenChange(next);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidate || issue || unchanged) return;
    setError(null);
    save.mutate(candidate, {
      onSuccess: () => onOpenChange(false),
      onError: (err) => setError(usernameErrorMessage(err)),
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={reset}
      size="sm"
      title={username ? "Change username" : "Choose a username"}
      description={`Other players see it in ${battle.plural}. Letters, numbers and underscores, 3 to 20 characters.`}
      footer={
        <ModalFooter>
          <ModalClose render={<Button type="button" variant="ghost" />}>Cancel</ModalClose>
          <Button
            type="submit"
            form={formId}
            disabled={!candidate || issue !== null || unchanged || save.isPending}
            loading={save.isPending}
          >
            {save.isPending ? "Saving…" : "Save username"}
          </Button>
        </ModalFooter>
      }
    >
      <form id={formId} className="flex flex-col gap-3" onSubmit={submit}>
        <Input
          label="Username"
          value={draft}
          maxLength={USERNAME_MAX_LENGTH}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          onChange={(e) => setDraft(e.target.value)}
          error={issue ? USERNAME_ISSUE_MESSAGE[issue] : undefined}
        />
        {error && <Alert tone="danger">{error}</Alert>}
      </form>
    </Modal>
  );
}
